import { type TFunction } from "i18next"

import { type AppointmentType, type Event } from "../../types/types.ts"
import { appointmentTypeLabelKey } from "../constants/appointment-types.ts"

type CalendarEventRecord = Pick<
  Event,
  "lead" | "project" | "contact" | "category"
> & {
  // The meeting's own address (arriving with the meeting-location work);
  // until every stored event carries it, the lead's contact address stands in.
  address?: string | null
}

interface CalendarEventCardInput {
  title?: string | null
  record?: CalendarEventRecord
}

// Only where the shared label reads badly as a headline; every other category
// resolves through appointmentTypeLabelKey so the card and the Appointment
// Type row cannot show two spellings of one type.
const HEADLINE_OVERRIDE_KEYS: Partial<Record<AppointmentType, string>> = {
  LEAD_MEETING: "calendarEvent.headline.meeting"
}

const calendarEventCardTitle = (
  { title, record }: CalendarEventCardInput,
  t: TFunction
): string => {
  const category = record?.category
  if (!category) return title || "-"

  return t(
    HEADLINE_OVERRIDE_KEYS[category] ?? appointmentTypeLabelKey(category)
  )
}

// The record name and the person's name are two distinct lines on the card
// (spec: TIME / TYPE / LEAD·PROJECT / PERSON), never joined into one string —
// that was the previous design and read as too dense.
const calendarEventCardRecordParts = (
  { record }: CalendarEventCardInput,
  t: TFunction
): { recordName: string | null; personName: string | null } => {
  const source = record?.project || record?.lead
  if (!source) return { recordName: null, personName: null }

  // The record's own name ("Cole Backyard Renovation") is what someone
  // scanning the grid actually recognizes — its uid is an internal
  // identifier with no meaning at a glance, so it never appears here.
  const kind = record?.project ? "project" : "lead"
  const recordName = source.name?.trim() || t(`calendarEvent.record.${kind}`)
  const personName = record?.contact?.customer_name?.trim() || null
  return { recordName, personName }
}

// The card's second row. A follow-up is a call, not a visit — it stays a
// one-line card with no address by design.
const calendarEventCardAddress = ({
  record
}: CalendarEventCardInput): string | null => {
  if (record?.category === "FOLLOW_UP") return null

  const own = record?.address?.trim()
  if (own) return own

  const contact = record?.contact
  const fallback = [contact?.address?.trim(), contact?.city?.trim()]
    .filter(Boolean)
    .join(", ")
  return fallback || null
}

export {
  calendarEventCardAddress,
  calendarEventCardRecordParts,
  calendarEventCardTitle
}
